import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Read the organization list `login` cached.
 *
 * Deliberately total: a corrupt or absent value returns [] rather than throwing,
 * because this renders in the app header and an exception here would blank the
 * chrome for every user.
 */
export function parseAvailableOrganizations(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Only surface the switcher to people who actually have a choice.
 *
 * Single-org users — which is everyone today except invited team members — see
 * no new chrome at all.
 */
export function shouldShowOrganizationSwitcher(organizations) {
  return Array.isArray(organizations) && organizations.length > 1;
}

/**
 * What must change in localStorage when the active organization changes.
 *
 * Returns null for a no-op so the caller can skip the page reload.
 *
 * `current_location_id` is cleared on purpose: locations are org-scoped, so
 * carrying the previous org's location into the new one sends a foreign id in
 * X-Location-Id and the backend rejects it ("Location not found in
 * organization") on every screen until the user notices.
 */
export function storageChangesForOrgSwitch(nextOrgId, currentOrgId) {
  if (!nextOrgId || nextOrgId === currentOrgId) return null;
  return { current_organization_id: nextOrgId, current_location_id: null };
}

const OrganizationSwitcher = () => {
  const [organizations, setOrganizations] = useState(() =>
    parseAvailableOrganizations(localStorage.getItem('available_organizations'))
  );
  const [isOpen, setIsOpen] = useState(false);

  const token = localStorage.getItem('token');
  const currentOrgId = localStorage.getItem('current_organization_id');

  useEffect(() => {
    // Re-resolve from the server so a membership granted mid-session (Swati
    // invites someone while they are logged in) appears without a re-login.
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!Array.isArray(data)) return;
        setOrganizations(data);
        localStorage.setItem('available_organizations', JSON.stringify(data));
      } catch {
        /* keep the cached list — the switcher must not break the header */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSelect = (org) => {
    const changes = storageChangesForOrgSwitch(org.id, currentOrgId);
    setIsOpen(false);
    if (!changes) return;

    localStorage.setItem('current_organization_id', changes.current_organization_id);
    localStorage.removeItem('current_location_id');
    window.dispatchEvent(new CustomEvent('organizationChanged', { detail: org }));
    // Every org-scoped screen caches its own data behind X-Organization-Id, so a
    // reload is the honest way to refetch all of them at once.
    window.location.reload();
  };

  if (!shouldShowOrganizationSwitcher(organizations)) return null;

  const current = organizations.find((o) => o.id === currentOrgId);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Switch organization"
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <Building2 className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {current ? current.name : 'Select Organization'}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
          <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            <div className="py-2">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelect(org)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between ${
                    currentOrgId === org.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{org.name}</p>
                      {Array.isArray(org.roles) && org.roles.length > 0 && (
                        <p className="text-xs text-gray-500">{org.roles.join(', ')}</p>
                      )}
                    </div>
                  </div>
                  {currentOrgId === org.id && <Check className="w-4 h-4 text-blue-600" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationSwitcher;
